// materialAdapter — two boundary codecs for the material command-surface.
//
// READ (surface A — render a server view-model): `parseShowResource` reads the
// SERVER-decoded material view-model off `showResource`'s `view.materialView`
// DTO (IDD 7ff38610, API-first). The server owns the decode of a material's
// stored content into the editor's `Element[]` + id-paths + provenance +
// template identity; the UI just reads that view-model with cast-free wireShape
// readers. It NEVER reconstructs durable material state itself — the former
// client-side content fold (the forbidden "third path") lived here and is gone.
//
// WRITE (surface B — an intent): `toWireElement` / `toWireChoice` serialise a
// palette-minted UI `Element` into the generic Content-tree node the domain's
// edit primitives address (`insert_element` / `insert_child_element`). This is
// the encode half of authoring, not a durable read — it stays.
//
// @bilda/client imports zero @bilda/core, so every wire shape here is plain JSON;
// the server's contract brands ids at ingress on the write path, and the read
// DTO arrives pre-decoded.

export { parseShowResource, toWireChoice, toWireElement };

import { exhaustSwitchCase, Result } from "@bilda/utils";
import type {
    Choice,
    Element,
    ElementId,
    HeadingElement,
} from "../components/Editor/types.js";
import type { ResourceRef } from "../components/Navigation/types.js";
import { type ExternalError, mkMalformed } from "./externalError.js";
import type { LoadedMaterial, MaterialProvenance } from "./ports.js";
import { bool, fields, list, num, str } from "./wireShape.js";

// === Boundary reader ==========================================================
// `showResource` (with `expectedKind: "material"`) returns
// `{ view: { resource: { …, contentHash }, materialView: { elements, pathById,
// provenance, templateRef } } }`. The server attaches `materialView` for every
// material, so its ABSENCE is the meaningful signal that the resource is not a
// material (or a shape surprise) — a typed Malformed error, never a guess. The
// raw `content` string is no longer read here: the UI does not decode it (the
// server did). `contentHash` is still read off the resource — a single field,
// the edit's optimistic-lock guard, not a fold.

const parseShowResource = (
    value: unknown,
): Result.Result<LoadedMaterial, ExternalError> => {
    const view = fields(fields(value)?.get("view"));
    const resource = fields(view?.get("resource"));
    if (resource === undefined) {
        return Result.err(mkMalformed("showResource: no resource in view"));
    }
    const materialView = fields(view?.get("materialView"));
    if (materialView === undefined) {
        return Result.err(mkMalformed("showResource: no materialView in view"));
    }
    // The optimistic-lock guard. Absent ⇒ empty string; the first edit then
    // fails the server's hash check and the host rolls back — a loud, safe
    // degradation rather than a silent unguarded write.
    const contentHash = str(resource.get("contentHash")) ?? "";
    return Result.ok({
        elements: readWireElements(list(materialView.get("elements"))),
        pathById: readWirePathById(materialView.get("pathById")),
        provenance: readProvenance(materialView.get("provenance")),
        templateRef: readTemplateRef(materialView.get("templateRef")),
        contentHash,
    });
};

// === Served view-model readers (cast-free wire → typed UI shapes) =============
// Each reader is total: a shape surprise degrades to "absent" (dropped element,
// empty field), never a throw — the same discipline every wireShape adapter
// uses. The server produces exactly these shapes (see @bilda/app MaterialView).

const readWireElements = (raw: readonly unknown[]): readonly Element[] =>
    raw.flatMap((entry): Element[] => {
        const node = fields(entry);
        const id = str(node?.get("id"));
        const type = str(node?.get("type"));
        if (node === undefined || id === undefined || type === undefined) {
            return [];
        }
        switch (type) {
            case "heading":
                return [
                    {
                        type: "heading",
                        id: eid(id),
                        level: readLevel(node.get("level")),
                        text: str(node.get("text")) ?? "",
                    },
                ];
            case "prose":
                return [
                    {
                        type: "prose",
                        id: eid(id),
                        markdown: str(node.get("markdown")) ?? "",
                    },
                ];
            case "objective":
                return [
                    {
                        type: "objective",
                        id: eid(id),
                        text: str(node.get("text")) ?? "",
                    },
                ];
            case "note":
                return [
                    {
                        type: "note",
                        id: eid(id),
                        text: str(node.get("text")) ?? "",
                    },
                ];
            case "mcq":
                return [
                    {
                        type: "mcq",
                        id: eid(id),
                        stem: str(node.get("stem")) ?? "",
                        choices: readWireChoices(node.get("choices")),
                        answerMode:
                            str(node.get("answerMode")) === "multiple"
                                ? "multiple"
                                : "single",
                    },
                ];
            case "part": {
                const target = str(node.get("target"));
                return target === undefined
                    ? []
                    : [{ type: "part", id: eid(id), target }];
            }
            default:
                // An element kind the editor doesn't render — drop it (the
                // server never emits one; a forward-compat guard, not a path).
                return [];
        }
    });

const readWireChoices = (raw: unknown): readonly Choice[] =>
    list(raw).flatMap((entry): Choice[] => {
        const choice = fields(entry);
        const id = str(choice?.get("id"));
        if (choice === undefined || id === undefined) return [];
        const feedback = str(choice.get("feedback"));
        return [
            {
                id: eid(id),
                text: str(choice.get("text")) ?? "",
                correct: bool(choice.get("correct")),
                ...(feedback === undefined ? {} : { feedback }),
            },
        ];
    });

// A heading level number (1-6), defaulting to 2. Total.
const readLevel = (v: unknown): HeadingElement["level"] =>
    v === 1 || v === 2 || v === 3 || v === 4 || v === 5 || v === 6 ? v : 2;

// `[id, path][]` entries → the id-path Map the host looks edits up in. A
// malformed entry (non-tuple, no string key) is skipped, never a crash.
const readWirePathById = (
    raw: unknown,
): ReadonlyMap<ElementId, readonly string[]> => {
    const entries: [ElementId, readonly string[]][] = [];
    for (const entry of list(raw)) {
        if (!Array.isArray(entry) || entry.length < 2) continue;
        const key = str(entry[0]);
        if (key === undefined) continue;
        const path = list(entry[1]).flatMap((seg) => {
            const s = str(seg);
            return s === undefined ? [] : [s];
        });
        entries.push([eid(key), path]);
    }
    return new Map(entries);
};

// The served provenance view-model. `derivedFrom` is already narrowed to the
// navigable ResourceRef kinds server-side (a `file` endpoint dropped there), so
// the switch only rebuilds the typed union; an unknown `type` degrades to
// dropped. `createdAt` absent ⇒ the host prints no creation line;
// `authoredByAgent` gates the "made with AI" disclosure.
const readProvenance = (raw: unknown): MaterialProvenance => {
    const p = fields(raw);
    return {
        authoredByAgent: bool(p?.get("authoredByAgent")),
        derivedFrom: list(p?.get("derivedFrom")).flatMap(readLineageRef),
        createdAt: str(p?.get("createdAt")),
    };
};

const readLineageRef = (raw: unknown): ResourceRef[] => {
    const entry = fields(raw);
    const id = str(entry?.get("id"));
    if (id === undefined) return [];
    const title = str(entry?.get("title")) ?? "Untitled";
    switch (str(entry?.get("type"))) {
        case "source":
            return [{ type: "source", id, title }];
        case "material":
            return [{ type: "material", id, title }];
        case "export":
            return [{ type: "export", id, title }];
        case "file":
            return [{ type: "file", id, title }];
        default:
            return [];
    }
};

// The material's template identity ({ id, version }); `undefined` when the DTO
// omits it (legacy material / shape surprise), so the host offers the full
// format list rather than gating on a template it can't read.
const readTemplateRef = (
    raw: unknown,
): { readonly id: string; readonly version: number } | undefined => {
    const ref = fields(raw);
    const id = str(ref?.get("id"));
    const version = num(ref?.get("version"));
    if (id === undefined || version === undefined) return undefined;
    return { id, version };
};

// === UI Element → domain wire node (the inverse codec) ========================
// A palette-minted UI Element, serialised into the generic Content-tree node
// the domain edit primitives address (`{ id, type, text?, children?, attrs? }`
// — resource/element.ts). @bilda/client imports zero @bilda/core, so the node is a
// plain JSON object; the server's contract (`refineElement`) brands the ids at
// ingress.
//   • heading  → { type:"heading",   text }          (level in attrs.level)
//   • prose    → { type:"prose",     text: markdown } (read back as a prose leaf)
//   • objective→ { type:"objective", text }
//   • mcq      → { type:"question",  text: stem, children:[option…] } — each
//     option carries text + `attrs.correct` (and `attrs.feedback` when set),
//     because the domain Element has no top-level answer-key slot.

// The plain JSON shape the domain's `refineElement` accepts. Named locally (not
// imported from the app contract) to keep @bilda/client free of @bilda/core.
export type WireElementNode = {
    readonly id: string;
    readonly type: string;
    readonly text?: string;
    readonly attrs?: Readonly<Record<string, unknown>>;
    readonly children?: readonly WireElementNode[];
};

const toWireElement = (el: Element): WireElementNode => {
    switch (el.type) {
        case "heading":
            // Level rides on `attrs.level` (the merge-safe attr slot), so a
            // heading round-trips its level through the generic tree.
            return {
                id: el.id,
                type: "heading",
                text: el.text,
                attrs: { level: el.level },
            };
        case "prose":
            return { id: el.id, type: "prose", text: el.markdown };
        case "objective":
            return { id: el.id, type: "objective", text: el.text };
        case "note":
            return { id: el.id, type: "note", text: el.text };
        case "mcq":
            return {
                id: el.id,
                type: "question",
                text: el.stem,
                attrs: { answerMode: el.answerMode },
                children: el.choices.map(toWireChoice),
            };
        // A part is never minted here (Create includes it); should one come
        // through, it goes as the include node the server refuses to make.
        case "part":
            return { id: el.id, type: "include" };
        default:
            return exhaustSwitchCase(el);
    }
};

// One MCQ choice → the domain `option` node. The answer key + optional coaching
// line live in `attrs` (the domain Element has no top-level `correct` slot).
// Shared by `toWireElement` (serialising a whole MCQ) and the `addChoice` port
// (serialising one appended choice).
const toWireChoice = (c: Choice): WireElementNode => ({
    id: c.id,
    type: "option",
    text: c.text,
    attrs: {
        correct: c.correct,
        ...(c.feedback !== undefined ? { feedback: c.feedback } : {}),
    },
});

// ElementId is a pass-through label (a plain string in the UI); the smart
// name keeps the intent readable at each call site.
const eid = (s: string): ElementId => s;
